/**
 * @fileoverview Accessibility (a11y) type definitions for the WebChat application
 *
 * This module provides type definitions for accessibility features including
 * scrolling parameters, navigation states, and other accessibility-related
 * interfaces to enhance type safety and code maintainability.
 *
 * @author Palash
 * @version 1.0.0
 */

//#region Scroll Types

/**
 * Interface for scroll function parameters to keep the API consistent
 * and make the code more maintainable
 */
export interface ScrollOptions {
	/** The element to scroll into view */
	element: HTMLElement;
	/** Whether the chat is loaded inside an iframe */
	isIFrameLoaded: boolean;
	/** Whether tiles are opened in the iframe */
	isTilesOpened: boolean;
	/** The chat body container element (optional) */
	chatBodyElement?: HTMLElement;
}

//#endregion

//#region Navigation State Types

/**
 * Base interface with common navigation state properties
 */
export interface BaseNavigationState {
	/** The CSS class prefix used in the application */
	prefixClassName: string;
	/** Whether the chat is loaded inside an iframe */
	isIFrameLoaded: boolean;
	/** Whether tiles are opened in the iframe */
	isTilesOpened: boolean;
}

/**
 * Navigation state for navigating to the top of the chat
 * Requires enableMessageNavigation parameter
 */
export interface TopNavigationState extends BaseNavigationState {
	/** Whether message navigation is enabled in settings */
	enableMessageNavigation: boolean; // Required for top navigation
	disableInput?: never; // Not used in top navigation
}

/**
 * Navigation state for navigating to the bottom of the chat
 * Requires disableInput parameter
 */
export interface BottomNavigationState extends BaseNavigationState {
	/** Whether the input is disabled */
	disableInput: boolean; // Required for bottom navigation
	enableMessageNavigation?: never; // Not used in bottom navigation
}

/**
 * Type alias that can represent either navigation state
 */
export type NavigationState = TopNavigationState | BottomNavigationState;

//#endregion
